import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  Divider,
  ToggleButton,
  ToggleButtonGroup,
  useTheme,
  alpha,
  Avatar,
  Paper,
  styled,
  Fade,
  Slide,
  Autocomplete
} from '@mui/material';
import {
  SwapHoriz as SwapIcon,
  ArrowForward as ArrowIcon,
  CurrencyExchange as ExchangeIcon,
  AccountBalanceWallet as WalletIcon,
  AttachMoney as MoneyIcon,
  CheckCircle as SuccessIcon,
  Payment as PaymentIcon,
  CreditCard as CreditCardIcon,
  AccountBalance as BankIcon,
  SecurityUpdateWarning as SecurityIcon,
  Person as PersonIcon,
  ContactMail as ContactIcon
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import memberService from '../../../services/memberService';

// Custom styled components
const StyledDialogTitle = styled(DialogTitle)(({ theme }) => ({
  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
  color: theme.palette.primary.contrastText,
  padding: theme.spacing(3),
  display: 'flex',
  alignItems: 'center',
}));

const CircleDecoration = styled('div')(({ theme, size = 100, top, right, left, bottom, opacity = 0.1 }) => ({
  position: 'absolute',
  width: size,
  height: size,
  borderRadius: '50%',
  background: alpha(theme.palette.common.white, opacity),
  top: top || 'auto',
  right: right || 'auto',
  left: left || 'auto',
  bottom: bottom || 'auto',
  zIndex: 0,
}));

const StyledToggleButtonGroup = styled(ToggleButtonGroup)(({ theme }) => ({
  width: '100%',
  '& .MuiToggleButtonGroup-grouped': {
    margin: theme.spacing(0.5),
    borderRadius: theme.shape.borderRadius * 2,
    border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
    fontWeight: 500,
    '&.Mui-selected': {
      borderColor: theme.palette.primary.main,
      backgroundColor: alpha(theme.palette.primary.main, 0.1),
      color: theme.palette.primary.main,
      boxShadow: `0 2px 10px ${alpha(theme.palette.primary.main, 0.3)}`,
      fontWeight: 700,
    },
    '&:not(:first-of-type)': {
      borderRadius: theme.shape.borderRadius * 2,
    },
    '&:first-of-type': {
      borderRadius: theme.shape.borderRadius * 2,
    },
  },
}));

const ExchangeField = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  backgroundColor: alpha(theme.palette.background.paper, 0.8),
  borderRadius: theme.shape.borderRadius * 2,
  border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
  padding: theme.spacing(2),
  marginBottom: theme.spacing(2),
  boxShadow: `0 4px 12px ${alpha(theme.palette.common.black, 0.05)}`,
}));

const SummaryBox = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  borderRadius: theme.shape.borderRadius * 2,
  backgroundColor: alpha(theme.palette.background.paper, 0.8),
  border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
  marginTop: theme.spacing(3),
  marginBottom: theme.spacing(3),
  boxShadow: `0 4px 12px ${alpha(theme.palette.common.black, 0.05)}`,
}));

const SummaryRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: theme.spacing(1, 0),
  '&:not(:last-child)': {
    borderBottom: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
  },
}));

const AmountTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    fontSize: '1.5rem',
    fontWeight: 'bold',
    '& fieldset': {
      borderRadius: theme.shape.borderRadius * 2,
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.primary.main,
      borderWidth: 2,
      boxShadow: `0 0 0 4px ${alpha(theme.palette.primary.main, 0.2)}`,
    },
  },
  '& .MuiInputLabel-root': {
    fontSize: '1.1rem',
    fontWeight: 500,
    '&.Mui-focused': {
      color: theme.palette.primary.main,
    },
  },
}));

const MethodButton = styled(Button)(({ theme, selected }) => ({
  borderRadius: theme.shape.borderRadius * 2,
  padding: theme.spacing(1.5),
  border: `1px solid ${selected ? theme.palette.primary.main : alpha(theme.palette.divider, 0.3)}`,
  backgroundColor: selected ? alpha(theme.palette.primary.main, 0.1) : 'transparent',
  color: selected ? theme.palette.primary.main : theme.palette.text.primary,
  '&:hover': {
    backgroundColor: selected ? alpha(theme.palette.primary.main, 0.15) : alpha(theme.palette.divider, 0.1),
  },
  boxShadow: selected ? `0 2px 10px ${alpha(theme.palette.primary.main, 0.3)}` : 'none',
}));

const ExchangeTokensModal = ({ open, onClose, onExchange, wallet }) => {
  const theme = useTheme();
  const [formData, setFormData] = useState({
    amount: '',
    direction: 'to_currency', // 'to_currency' or 'to_tokens'
    payoutMethod: 'wallet',
    recipientId: '', // Added for when transferring to another member
    recipientName: '' // Added for display purposes
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [exchangeStage, setExchangeStage] = useState('form'); // form, pending, success
  const [memberOptions, setMemberOptions] = useState([]);
  const [membersLoading, setMembersLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRecipient, setSelectedRecipient] = useState(null);

  useEffect(() => {
    if (open) {
      // Reset form when modal opens
      setFormData({
        amount: '',
        direction: 'to_currency',
        payoutMethod: 'wallet',
        recipientId: '',
        recipientName: ''
      });
      setExchangeStage('form');
      setError(null);
      setSelectedRecipient(null);
      
      // Load initial member list
      loadInitialMembers();
    }
  }, [open]);

  useEffect(() => {
    if (searchTerm.length > 2) {
      searchMembers(searchTerm);
    }
  }, [searchTerm]);

  const loadInitialMembers = async () => {
    setMembersLoading(true);
    try {
      const tenantId = localStorage.getItem('tenantId');
      if (!tenantId) return;

      const filters = { tenantId, status: 'active' };
      const response = await memberService.getAllMembers(1, 50, '', filters);

      let memberList = [];
      if (response && response.data && Array.isArray(response.data)) {
        memberList = response.data;
      } else if (response && Array.isArray(response)) {
        memberList = response;
      } else if (response && response.members && Array.isArray(response.members)) {
        memberList = response.members;
      } else {
        console.warn('Unexpected member response format', response);
        return;
      }

      const processedMembers = memberList.map((member) => {
        if (member.userId && typeof member.userId === 'object') {
          const firstName = member.userId.firstName || '';
          const lastName = member.userId.lastName || '';
          const fullName = `${firstName} ${lastName}`.trim();

          return {
            _id: member._id,
            name: fullName || 'Unnamed Member',
            email: member.userId.email || 'No email',
            status: member.status || 'unknown',
          };
        }

        const firstName = member.firstName || '';
        const lastName = member.lastName || '';
        const fullName = `${firstName} ${lastName}`.trim();

        return {
          _id: member._id,
          name: fullName || member.name || 'Unnamed Member',
          email: member.email || 'No email',
          status: member.status || 'unknown',
        };
      });

      setMemberOptions(processedMembers);
    } catch (error) {
      console.error('Error loading initial members:', error);
    } finally {
      setMembersLoading(false);
    }
  };

  const searchMembers = async (query) => {
    if (!query || query.length < 2) return;

    setMembersLoading(true);
    try {
      const tenantId = localStorage.getItem('tenantId');
      if (!tenantId) return;

      const filters = { tenantId };
      const response = await memberService.getAllMembers(1, 20, query, filters);

      let memberList = [];
      if (response && response.data && Array.isArray(response.data)) {
        memberList = response.data;
      } else if (response && Array.isArray(response)) {
        memberList = response;
      } else if (response && response.members && Array.isArray(response.members)) {
        memberList = response.members;
      } else {
        console.warn('Unexpected member search response format', response);
        return;
      }

      const processedMembers = memberList.map((member) => {
        if (member.userId && typeof member.userId === 'object') {
          const firstName = member.userId.firstName || '';
          const lastName = member.userId.lastName || '';
          const fullName = `${firstName} ${lastName}`.trim();

          return {
            _id: member._id,
            name: fullName || 'Unnamed Member',
            email: member.userId.email || 'No email',
            status: member.status || 'unknown',
          };
        }

        const firstName = member.firstName || '';
        const lastName = member.lastName || '';
        const fullName = `${firstName} ${lastName}`.trim();

        return {
          _id: member._id,
          name: fullName || member.name || 'Unnamed Member',
          email: member.email || 'No email',
          status: member.status || 'unknown',
        };
      });

      const activeMembers = processedMembers.filter((m) => m.status !== 'inactive');
      setMemberOptions(activeMembers);
    } catch (error) {
      console.error('Error searching members:', error);
    } finally {
      setMembersLoading(false);
    }
  };

  const handleRecipientChange = (event, value) => {
    if (value) {
      setSelectedRecipient(value);
      setFormData({
        ...formData,
        recipientId: value._id,
        recipientName: value.name
      });
    } else {
      setSelectedRecipient(null);
      setFormData({
        ...formData,
        recipientId: '',
        recipientName: ''
      });
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const handleDirectionChange = (e, newDirection) => {
    if (newDirection !== null) {
      setFormData({
        ...formData,
        amount: '',
        direction: newDirection
      });
    }
  };

  const calculateEquivalent = () => {
    if (!wallet?.token?.baseRate || !formData.amount) return '0';
    
    const baseRate = wallet.token.baseRate;
    const amount = parseFloat(formData.amount);
    
    if (isNaN(amount)) return '0';
    
    if (formData.direction === 'to_currency') {
      // Token to currency: amount * baseRate
      return (amount * baseRate).toFixed(2);
    } else {
      // Currency to token: amount / baseRate
      return (amount / baseRate).toFixed(2);
    }
  };

  const getFeeAmount = () => {
    const equivalentAmount = parseFloat(calculateEquivalent());
    if (isNaN(equivalentAmount)) return '0';
    
    // Typically exchanges have platform fees
    const platformFeePercent = wallet?.settings?.platformFeePercent || 5;
    return ((equivalentAmount * platformFeePercent) / 100).toFixed(2);
  };

  const getFinalAmount = () => {
    const equivalentAmount = parseFloat(calculateEquivalent());
    const feeAmount = parseFloat(getFeeAmount());
    
    if (isNaN(equivalentAmount) || isNaN(feeAmount)) return '0';
    
    return (equivalentAmount - feeAmount).toFixed(2);
  };

  const validateForm = () => {
    const amount = parseFloat(formData.amount);
    if (isNaN(amount) || amount <= 0) {
      setError('Please enter a valid amount greater than 0');
      return false;
    }

    if (formData.direction === 'to_currency' && amount > wallet.balance) {
      setError(`You don't have enough tokens. Your balance is ${wallet.balance} ${wallet.token?.symbol || 'tokens'}`);
      return false;
    }

    if (!formData.payoutMethod && formData.direction === 'to_currency') {
      setError('Please select a payout method');
      return false;
    }

    // If the payout method is 'transfer' but no recipient is selected
    if (formData.payoutMethod === 'transfer' && !formData.recipientId) {
      setError('Please select a recipient for the transfer');
      return false;
    }

    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setLoading(true);
    setError(null);
    setExchangeStage('pending');

    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      await onExchange({
        ...formData,
        amount: parseFloat(formData.amount)
      });
      
      setExchangeStage('success');
    } catch (err) {
      setError(err.message || 'An error occurred during exchange');
      setExchangeStage('form');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setError(null);
    onClose();
  };

  const getPaymentMethodIcon = (method) => {
    switch (method) {
      case 'wallet':
        return <WalletIcon />;
      case 'bank':
        return <BankIcon />;
      case 'paypal':
        return <PaymentIcon />;
      case 'transfer':
        return <PersonIcon />;
      default:
        return <CreditCardIcon />;
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose} 
      maxWidth="sm" 
      fullWidth
      PaperProps={{
        component: motion.div,
        initial: { opacity: 0, y: 20 },
        animate: { opacity: 1, y: 0 },
        transition: { duration: 0.3 },
        sx: { 
          borderRadius: theme.shape.borderRadius * 3,
          overflow: 'hidden',
          boxShadow: '0 10px 40px rgba(0, 0, 0, 0.15)'
        }
      }}
    >
      <StyledDialogTitle>
        <Avatar 
          sx={{ 
            bgcolor: 'rgba(255, 255, 255, 0.2)', 
            mr: 2, 
            p: 0.5,
            boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)'
          }}
        >
          <ExchangeIcon />
        </Avatar>
        <Typography variant="h6" component="div" fontWeight="bold">
          Exchange Tokens
        </Typography>
      </StyledDialogTitle>
      
      <DialogContent sx={{ position: 'relative', p: 3 }}>
        <CircleDecoration size={200} top="-100px" right="-100px" opacity={0.03} />
        <CircleDecoration size={150} bottom="-50px" left="-50px" opacity={0.03} />
        
        {exchangeStage === 'success' ? (
          <Box 
            component={motion.div}
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}
            sx={{ 
              textAlign: 'center', 
              py: 4, 
              position: 'relative', 
              zIndex: 1,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center'
            }}
          >
            <Avatar
              sx={{
                width: 80,
                height: 80,
                mb: 2,
                bgcolor: alpha(theme.palette.success.main, 0.1),
                color: theme.palette.success.main,
                boxShadow: `0 0 0 8px ${alpha(theme.palette.success.main, 0.1)}`
              }}
            >
              <SuccessIcon sx={{ fontSize: 40 }} />
            </Avatar>
            
            <Typography variant="h5" gutterBottom fontWeight="bold">
              Exchange Successful!
            </Typography>
            
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3, maxWidth: 400 }}>
              {formData.direction === 'to_currency' 
                ? `You have successfully exchanged ${formData.amount} ${wallet.token?.symbol} for $${getFinalAmount()}.`
                : `You have successfully exchanged $${formData.amount} for ${getFinalAmount()} ${wallet.token?.symbol}.`
              }
            </Typography>
            
            <SummaryBox 
              elevation={0} 
              sx={{ 
                p: 3, 
                width: '100%', 
                bgcolor: alpha(theme.palette.success.main, 0.05),
                border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`,
                borderRadius: theme.shape.borderRadius * 2
              }}
            >
              <SummaryRow>
                <Typography variant="subtitle2" color="text.secondary">Exchange Rate:</Typography>
                <Typography variant="subtitle1" fontWeight="bold">
                  1 {wallet?.token?.symbol || 'token'} = ${wallet?.token?.baseRate || '0.00'} USD
                </Typography>
              </SummaryRow>
              
              <SummaryRow>
                <Typography variant="subtitle2" color="text.secondary">
                  {formData.direction === 'to_currency' ? 'Tokens Exchanged:' : 'USD Exchanged:'}
                </Typography>
                <Typography variant="subtitle1" fontWeight="bold">
                  {formData.direction === 'to_currency' 
                    ? `${formData.amount} ${wallet.token?.symbol}` 
                    : `$${formData.amount}`
                  }
                </Typography>
              </SummaryRow>
              
              <SummaryRow>
                <Typography variant="subtitle2" color="text.secondary">Fee:</Typography>
                <Typography variant="subtitle1" fontWeight="bold" color="error.main">
                  ${getFeeAmount()}
                </Typography>
              </SummaryRow>
              
              <SummaryRow>
                <Typography variant="subtitle2" color="text.secondary">
                  {formData.direction === 'to_currency' ? 'USD Received:' : 'Tokens Received:'}
                </Typography>
                <Typography variant="h6" fontWeight="bold" color="success.main">
                  {formData.direction === 'to_currency' 
                    ? `$${getFinalAmount()}` 
                    : `${getFinalAmount()} ${wallet.token?.symbol}`
                  }
                </Typography>
              </SummaryRow>
              
              {formData.direction === 'to_currency' && formData.payoutMethod === 'transfer' && (
                <SummaryRow>
                  <Typography variant="subtitle2" color="text.secondary">Recipient:</Typography>
                  <Typography variant="subtitle1" fontWeight="medium">
                    {formData.recipientName}
                  </Typography>
                </SummaryRow>
              )}
              
              {formData.direction === 'to_currency' && formData.payoutMethod !== 'transfer' && (
                <SummaryRow>
                  <Typography variant="subtitle2" color="text.secondary">Payout Method:</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Avatar 
                      sx={{ 
                        width: 24, 
                        height: 24, 
                        mr: 1, 
                        bgcolor: alpha(theme.palette.primary.main, 0.1),
                        color: theme.palette.primary.main
                      }}
                    >
                      {getPaymentMethodIcon(formData.payoutMethod)}
                    </Avatar>
                    <Typography variant="subtitle1" fontWeight="medium">
                      {formData.payoutMethod === 'wallet' ? 'Account Wallet' : 
                       formData.payoutMethod === 'bank' ? 'Bank Account' : 
                       formData.payoutMethod === 'paypal' ? 'PayPal' : formData.payoutMethod}
                    </Typography>
                  </Box>
                </SummaryRow>
              )}
            </SummaryBox>
          </Box>
        ) : exchangeStage === 'pending' ? (
          <Box 
            component={motion.div}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.3 }}
            sx={{ 
              textAlign: 'center', 
              py: 4, 
              position: 'relative', 
              zIndex: 1,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center'
            }}
          >
            <Avatar
              sx={{
                width: 80,
                height: 80,
                mb: 2,
                bgcolor: alpha(theme.palette.primary.main, 0.1),
                color: theme.palette.primary.main
              }}
            >
              <CircularProgress size={40} />
            </Avatar>
            
            <Typography variant="h5" gutterBottom fontWeight="bold">
              Processing Exchange
            </Typography>
            
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3, maxWidth: 400 }}>
              {formData.direction === 'to_currency' 
                ? `Please wait while we process your exchange of ${formData.amount} ${wallet.token?.symbol} to USD.`
                : `Please wait while we process your exchange of $${formData.amount} to ${wallet.token?.symbol}.`
              }
            </Typography>
            
            <Box sx={{ width: '100%', position: 'relative' }}>
              <Box 
                component={motion.div} 
                initial={{ width: 0 }}
                animate={{ width: '100%' }}
                transition={{ duration: 2 }}
                sx={{ 
                  height: 6,
                  background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                  borderRadius: 3
                }}
              />
            </Box>
          </Box>
        ) : (
          <Box 
            component={motion.div}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.3 }}
            sx={{ position: 'relative', zIndex: 1 }}
          >
            {error && (
              <Alert 
                severity="error" 
                sx={{ mb: 3, borderRadius: theme.shape.borderRadius * 2 }}
              >
                {error}
              </Alert>
            )}
            
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              Exchange Direction
            </Typography>
            
            <StyledToggleButtonGroup
              value={formData.direction}
              exclusive
              onChange={handleDirectionChange}
              fullWidth
              sx={{ mb: 3 }}
            >
              <ToggleButton 
                value="to_currency"
                sx={{ display: 'flex', flexDirection: 'column', textTransform: 'none', py: 1.5 }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                  <SwapIcon sx={{ mr: 1, transform: 'rotate(90deg)' }} />
                  Tokens to Cash
                </Box>
                <Typography variant="caption" color="text.secondary">
                  Convert your tokens to USD
                </Typography>
              </ToggleButton>
              <ToggleButton 
                value="to_tokens"
                sx={{ display: 'flex', flexDirection: 'column', textTransform: 'none', py: 1.5 }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                  <SwapIcon sx={{ mr: 1, transform: 'rotate(-90deg)' }} />
                  Cash to Tokens
                </Box>
                <Typography variant="caption" color="text.secondary">
                  Buy tokens with USD
                </Typography>
              </ToggleButton>
            </StyledToggleButtonGroup>
            
            <ExchangeField>
              <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                {formData.direction === 'to_currency' ? 'I want to exchange' : 'I want to purchase for'}
              </Typography>
              
              <AmountTextField
                required
                fullWidth
                label={formData.direction === 'to_currency' ? 'Token Amount' : 'USD Amount'}
                name="amount"
                type="number"
                value={formData.amount}
                onChange={handleChange}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      {formData.direction === 'to_currency' ? (
                        <Avatar
                          src={wallet?.token?.logoUrl || null}
                          sx={{ width: 24, height: 24, mr: 1, bgcolor: alpha(theme.palette.primary.main, 0.1) }}
                        >
                          <WalletIcon fontSize="small" />
                        </Avatar>
                      ) : (
                        <MoneyIcon color="success" />
                      )}
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <Typography fontWeight="bold" color={formData.direction === 'to_currency' ? 'primary' : 'success.main'}>
                        {formData.direction === 'to_currency' ? wallet?.token?.symbol || 'tokens' : 'USD'}
                      </Typography>
                    </InputAdornment>
                  ),
                  inputProps: { min: 0, step: 'any' }
                }}
                placeholder="0.00"
                sx={{ mb: 2 }}
              />
              
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', my: 2 }}>
                <Divider sx={{ width: '40%', opacity: 0.5 }} />
                <Avatar
                  sx={{
                    width: 36,
                    height: 36,
                    mx: 2,
                    bgcolor: alpha(theme.palette.primary.main, 0.1),
                    color: theme.palette.primary.main
                  }}
                >
                  <ArrowIcon />
                </Avatar>
                <Divider sx={{ width: '40%', opacity: 0.5 }} />
              </Box>
              
              <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                {formData.direction === 'to_currency' ? 'I will receive' : 'I will receive'}
              </Typography>
              
              <TextField
                disabled
                fullWidth
                label={formData.direction === 'to_currency' ? 'USD Amount' : 'Token Amount'}
                type="text"
                value={formData.amount ? calculateEquivalent() : '0.00'}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      {formData.direction === 'to_currency' ? (
                        <MoneyIcon color="success" />
                      ) : (
                        <Avatar
                          src={wallet?.token?.logoUrl || null}
                          sx={{ width: 24, height: 24, mr: 1, bgcolor: alpha(theme.palette.primary.main, 0.1) }}
                        >
                          <WalletIcon fontSize="small" />
                        </Avatar>
                      )}
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <Typography fontWeight="bold" color={formData.direction === 'to_currency' ? 'success.main' : 'primary'}>
                        {formData.direction === 'to_currency' ? 'USD' : wallet?.token?.symbol || 'tokens'}
                      </Typography>
                    </InputAdornment>
                  )
                }}
              />
              
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                mt: 2,
                p: 1.5,
                bgcolor: alpha(theme.palette.background.default, 0.5),
                borderRadius: theme.shape.borderRadius,
                border: `1px dashed ${alpha(theme.palette.divider, 0.5)}`
              }}>
                <Typography variant="body2" color="text.secondary">
                  {formData.direction === 'to_currency' ? 'Your balance:' : 'Exchange rate:'}
                </Typography>
                <Typography variant="body2" fontWeight="medium">
                  {formData.direction === 'to_currency' 
                    ? `${wallet?.balance || 0} ${wallet?.token?.symbol || 'tokens'}`
                    : `1 USD = ${wallet?.token?.baseRate ? (1 / wallet.token.baseRate).toFixed(4) : '0'} ${wallet?.token?.symbol || 'tokens'}`
                  }
                </Typography>
              </Box>
            </ExchangeField>
            
            {formData.direction === 'to_currency' && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                  Payout Method
                </Typography>
                
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                  <MethodButton
                    fullWidth
                    selected={formData.payoutMethod === 'wallet'}
                    onClick={() => setFormData({ ...formData, payoutMethod: 'wallet' })}
                    sx={{ flex: '1 1 45%', minWidth: 140 }}
                  >
                    <Avatar sx={{ mr: 1, bgcolor: alpha(theme.palette.primary.main, 0.1), color: theme.palette.primary.main }}>
                      <WalletIcon />
                    </Avatar>
                    <Box sx={{ textAlign: 'left' }}>
                      <Typography variant="body2" fontWeight="medium">Account Wallet</Typography>
                      <Typography variant="caption" color="text.secondary">Funds added to your balance</Typography>
                    </Box>
                  </MethodButton>
                  
                  <MethodButton
                    fullWidth
                    selected={formData.payoutMethod === 'bank'}
                    onClick={() => setFormData({ ...formData, payoutMethod: 'bank' })}
                    sx={{ flex: '1 1 45%', minWidth: 140 }}
                  >
                    <Avatar sx={{ mr: 1, bgcolor: alpha(theme.palette.primary.main, 0.1), color: theme.palette.primary.main }}>
                      <BankIcon />
                    </Avatar>
                    <Box sx={{ textAlign: 'left' }}>
                      <Typography variant="body2" fontWeight="medium">Bank Account</Typography>
                      <Typography variant="caption" color="text.secondary">Direct deposit to your bank</Typography>
                    </Box>
                  </MethodButton>
                  
                  <MethodButton
                    fullWidth
                    selected={formData.payoutMethod === 'paypal'}
                    onClick={() => setFormData({ ...formData, payoutMethod: 'paypal' })}
                    sx={{ flex: '1 1 45%', minWidth: 140 }}
                  >
                    <Avatar sx={{ mr: 1, bgcolor: alpha(theme.palette.primary.main, 0.1), color: theme.palette.primary.main }}>
                      <PaymentIcon />
                    </Avatar>
                    <Box sx={{ textAlign: 'left' }}>
                      <Typography variant="body2" fontWeight="medium">PayPal</Typography>
                      <Typography variant="caption" color="text.secondary">Send to your PayPal account</Typography>
                    </Box>
                  </MethodButton>
                  
                  <MethodButton
                    fullWidth
                    selected={formData.payoutMethod === 'transfer'}
                    onClick={() => setFormData({ ...formData, payoutMethod: 'transfer' })}
                    sx={{ flex: '1 1 45%', minWidth: 140 }}
                  >
                    <Avatar sx={{ mr: 1, bgcolor: alpha(theme.palette.primary.main, 0.1), color: theme.palette.primary.main }}>
                      <PersonIcon />
                    </Avatar>
                    <Box sx={{ textAlign: 'left' }}>
                      <Typography variant="body2" fontWeight="medium">Transfer to Member</Typography>
                      <Typography variant="caption" color="text.secondary">Send to another member</Typography>
                    </Box>
                  </MethodButton>
                </Box>
                
                {/* Add recipient selection for transfer method */}
                {formData.payoutMethod === 'transfer' && (
                  <Box sx={{ mt: 2 }}>
                    <Autocomplete
                      id="recipient-select"
                      options={memberOptions}
                      getOptionLabel={(option) => `${option.name} (${option.email})`}
                      loading={membersLoading}
                      value={selectedRecipient}
                      onChange={handleRecipientChange}
                      onInputChange={(event, newInputValue) => {
                        setSearchTerm(newInputValue);
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Recipient"
                          placeholder={membersLoading ? "Loading members..." : "Search members..."}
                          error={formData.payoutMethod === 'transfer' && !formData.recipientId}
                          helperText={formData.payoutMethod === 'transfer' && !formData.recipientId ? "Please select a recipient" : ""}
                          InputProps={{
                            ...params.InputProps,
                            startAdornment: (
                              <InputAdornment position="start">
                                <ContactIcon color="primary" />
                              </InputAdornment>
                            ),
                            endAdornment: (
                              <>
                                {membersLoading ? <CircularProgress color="inherit" size={20} /> : null}
                                {params.InputProps.endAdornment}
                              </>
                            ),
                            sx: { borderRadius: theme.shape.borderRadius * 2 }
                          }}
                        />
                      )}
                      renderOption={(props, option) => (
                        <li {...props}>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Avatar sx={{ width: 36, height: 36, mr: 1.5, bgcolor: alpha(theme.palette.primary.main, 0.1) }}>
                              <PersonIcon color="primary" />
                            </Avatar>
                            <Box>
                              <Typography variant="body1">
                                {option.name}
                              </Typography>
                              {option.email && (
                                <Typography variant="caption" color="text.secondary">
                                  {option.email}
                                </Typography>
                              )}
                            </Box>
                          </Box>
                        </li>
                      )}
                    />
                  </Box>
                )}
              </Box>
            )}
            
            {formData.amount && !isNaN(parseFloat(formData.amount)) && parseFloat(formData.amount) > 0 && (
              <Fade in={true}>
                <SummaryBox>
                  <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                    Exchange Summary
                  </Typography>
                  
                  <SummaryRow>
                    <Typography variant="body2" color="text.secondary">Exchange Rate:</Typography>
                    <Typography variant="body2" fontWeight="bold">
                      1 {wallet?.token?.symbol || 'token'} = ${wallet?.token?.baseRate || '0.00'} USD
                    </Typography>
                  </SummaryRow>
                  
                  <SummaryRow>
                    <Typography variant="body2" color="text.secondary">
                      {formData.direction === 'to_currency' ? 'You Send:' : 'You Pay:'}
                    </Typography>
                    <Typography variant="body2" fontWeight="bold">
                      {formData.direction === 'to_currency' 
                        ? `${formData.amount} ${wallet?.token?.symbol || 'tokens'}` 
                        : `$${formData.amount}`
                      }
                    </Typography>
                  </SummaryRow>
                  
                  <SummaryRow>
                    <Typography variant="body2" color="text.secondary">Equivalent Value:</Typography>
                    <Typography variant="body2" fontWeight="bold">
                      {formData.direction === 'to_currency' 
                        ? `$${calculateEquivalent()}` 
                        : `${calculateEquivalent()} ${wallet?.token?.symbol || 'tokens'}`
                      }
                    </Typography>
                  </SummaryRow>
                  
                  <SummaryRow>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Typography variant="body2" color="text.secondary">
                        Platform Fee ({wallet?.settings?.platformFeePercent || 5}%):
                      </Typography>
                      <SecurityIcon fontSize="small" sx={{ ml: 0.5, color: 'text.secondary', fontSize: 16 }} />
                    </Box>
                    <Typography variant="body2" fontWeight="bold" color="error.main">
                      -${getFeeAmount()}
                    </Typography>
                  </SummaryRow>
                  
                  <SummaryRow>
                    <Typography variant="subtitle2">You Receive:</Typography>
                    <Typography variant="subtitle1" fontWeight="bold" color="success.main">
                      {formData.direction === 'to_currency' 
                        ? `$${getFinalAmount()}` 
                        : `${getFinalAmount()} ${wallet?.token?.symbol || 'tokens'}`
                      }
                    </Typography>
                  </SummaryRow>
                  
                  {formData.direction === 'to_currency' && formData.payoutMethod === 'transfer' && (
                    <SummaryRow>
                      <Typography variant="body2" color="text.secondary">Recipient:</Typography>
                      <Typography variant="body2" fontWeight="medium">
                        {formData.recipientName}
                      </Typography>
                    </SummaryRow>
                  )}
                </SummaryBox>
              </Fade>
            )}
            
            <Alert 
              severity="info" 
              variant="outlined"
              icon={<SecurityIcon />}
              sx={{ mt: 2, borderRadius: theme.shape.borderRadius * 2 }}
            >
              Exchange rates and fees are subject to change. The final amount may vary slightly at the time of processing.
            </Alert>
          </Box>
        )}
      </DialogContent>
      
      <DialogActions sx={{ p: 3, position: 'relative', zIndex: 1 }}>
        {exchangeStage === 'success' ? (
          <Button 
            onClick={handleClose} 
            variant="contained"
            color="primary"
            fullWidth
            sx={{ borderRadius: theme.shape.borderRadius * 2, py: 1.5, fontWeight: 'bold' }}
          >
            Done
          </Button>
        ) : exchangeStage === 'pending' ? (
          <Button 
            onClick={handleClose} 
            color="inherit"
            disabled
            sx={{ borderRadius: theme.shape.borderRadius * 2 }}
          >
            Processing...
          </Button>
        ) : (
          <>
            <Button 
              onClick={handleClose} 
              color="inherit"
              sx={{ borderRadius: theme.shape.borderRadius * 2 }}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleSubmit} 
              variant="contained"
              color="primary"
              disabled={
                loading || 
                !formData.amount || 
                (formData.direction === 'to_currency' && formData.amount > wallet?.balance) ||
                !formData.payoutMethod ||
                (formData.payoutMethod === 'transfer' && !formData.recipientId)
              }
              startIcon={<ExchangeIcon />}
              sx={{ borderRadius: theme.shape.borderRadius * 2, px: 3, py: 1, fontWeight: 'bold' }}
            >
              {loading ? <CircularProgress size={24} /> : 'Execute Exchange'}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ExchangeTokensModal;